import { useCallback, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

/*
 * Full-screen image viewer. No dependency beyond framer-motion, which is
 * already bundled. Keyboard: Esc closes, arrows navigate.
 */
export default function Lightbox({
  images,
  index,
  onClose,
  onNavigate,
  rtl,
}: {
  images: string[];
  index: number | null;
  onClose: () => void;
  onNavigate: (next: number) => void;
  rtl: boolean;
}) {
  const open = index !== null;

  const next = useCallback(
    () => index !== null && onNavigate((index + 1) % images.length),
    [index, images.length, onNavigate],
  );
  const prev = useCallback(
    () => index !== null && onNavigate((index - 1 + images.length) % images.length),
    [index, images.length, onNavigate],
  );

  useEffect(() => {
    if (!open) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") rtl ? prev() : next();
      else if (e.key === "ArrowLeft") rtl ? next() : prev();
    };

    /* Stop the page behind from scrolling while the viewer is open. */
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);

    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose, next, prev, rtl]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          onClick={onClose}
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-[90] bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10"
        >
          <button
            onClick={onClose}
            aria-label="Close"
            className="absolute top-5 ltr:right-5 rtl:left-5 p-3 rounded-full border border-border/20
                       text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              prev();
            }}
            aria-label="Previous image"
            className="absolute ltr:left-4 rtl:right-4 p-3 md:p-4 rounded-full border border-border/20
                       text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors z-10"
          >
            {rtl ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              next();
            }}
            aria-label="Next image"
            className="absolute ltr:right-4 rtl:left-4 p-3 md:p-4 rounded-full border border-border/20
                       text-foreground/80 hover:text-primary hover:border-primary/50 transition-colors z-10"
          >
            {rtl ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
          </button>

          <motion.img
            key={index}
            src={images[index!]}
            alt=""
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain rounded-lg select-none"
          />

          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-sm text-muted-foreground tabular-nums">
            {index! + 1} / {images.length}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
